// A card's Oracle text, from its MTGJSON row, which the router serves at /cards/<name>. The
// projection carries no rules text, so the hover preview reads it from here.

type CardData = {
  mana_cost: string | null; type_line: string; oracle_text: string | null; power: string | null; toughness: string | null;
  faces?: FaceData[];
};

// One face of a double-faced card's row.
type FaceData = { name: string; type_line: string; oracle_text: string | null };

export type OracleInfo = { type_line: string; oracle_text: string; mana_cost: string; power_toughness: string };

export class OracleCache {
  // A stored null means fetched-but-absent or in flight, so a miss is never refetched on every hover.
  private readonly known = new Map<string, OracleInfo | null>();

  constructor(private readonly onFetched: () => void) {}

  get(name: string): OracleInfo | null {
    return this.known.get(name) ?? null;
  }

  async fetch(name: string): Promise<void> {
    if (!name || this.known.has(name)) return;
    this.known.set(name, null);
    try {
      const r = await fetch(`/cards/${encodeURIComponent(name)}`);
      if (r.ok) {
        const card = (await r.json()) as CardData;
        this.known.set(name, oracleInfoOf(card));
        // The router knows a double-faced card by its front's name only, so its back is read from there.
        for (const face of card.faces ?? []) if (face.name !== name) this.known.set(face.name, faceInfoOf(face));
      }
    } catch { /* the null marker stays: the preview falls back to what the projection says */ }
    this.onFetched();
  }
}

function faceInfoOf(face: FaceData): OracleInfo {
  return { type_line: face.type_line, oracle_text: face.oracle_text ?? "", mana_cost: "", power_toughness: "" };
}

function oracleInfoOf(card: CardData): OracleInfo {
  return {
    type_line: card.type_line,
    oracle_text: card.oracle_text ?? "",
    mana_cost: card.mana_cost ?? "",
    power_toughness: card.power !== null && card.toughness !== null ? `${card.power}/${card.toughness}` : "",
  };
}
